import type { ReactNode } from 'react'
import { safeHttpUrl } from '../utils/safeUrl'

interface Props {
  text: string
  /** Optional override for paragraph margin (small content vs. body docs). */
  compact?: boolean
}

/**
 * Lightweight markdown renderer used in help modals and the submission upload
 * guide. Avoids a runtime dependency on `react-markdown`.
 *
 * Supported syntax:
 *   - Headings: `# H1` … `#### H4`
 *   - Paragraphs (blank-line separated)
 *   - Bullet lists: `- item` or `* item`
 *   - Ordered lists: `1. item`
 *   - Blockquotes: `> quoted` (rendered recursively, so a `> | … |` table works)
 *   - GFM-style tables (pipe syntax with a `---` separator row)
 *   - Inline bold (`**text**`), italic (`*text*`), and code (`` `x` ``)
 *   - Inline links: `[label](url)`
 */
export function Markdown({ text, compact = false }: Props) {
  const blocks: ReactNode[] = []
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]!

    // Blank lines just close the current block.
    if (!line.trim()) {
      i++
      continue
    }

    // Heading
    const h = line.match(/^(#{1,4})\s+(.*)$/)
    if (h) {
      const level = h[1]!.length
      const HTag = (`h${Math.min(level + 1, 6)}`) as keyof React.JSX.IntrinsicElements
      blocks.push(
        <HTag
          key={key++}
          style={{
            fontSize: level === 1 ? '1.25rem' : level === 2 ? '1.05rem' : '0.95rem',
            margin: level === 1 ? '0 0 0.5rem' : '1rem 0 0.4rem',
            color: 'var(--fg)',
          }}
        >
          {renderInline(h[2]!)}
        </HTag>,
      )
      i++
      continue
    }

    // Blockquote: gather consecutive `>`-prefixed lines, strip the marker, and
    // render the inner markdown (which may itself be a paragraph, list, or table)
    // inside a styled quote. Without this a `> | … |` table renders as raw pipes.
    if (line.trimStart().startsWith('>')) {
      const inner: string[] = []
      while (i < lines.length && lines[i]!.trimStart().startsWith('>')) {
        inner.push(lines[i]!.replace(/^\s*>\s?/, ''))
        i++
      }
      blocks.push(
        <blockquote
          key={key++}
          style={{
            borderLeft: '3px solid var(--border)',
            margin: compact ? '0.45rem 0' : '0.75rem 0',
            padding: '0.1rem 0 0.1rem 0.85rem',
            color: 'var(--muted)',
          }}
        >
          <Markdown text={inner.join('\n')} compact={compact} />
        </blockquote>,
      )
      continue
    }

    // Table (pipe-syntax, two-line minimum: header + separator)
    if (line.includes('|') && lines[i + 1] && /^\s*\|?\s*:?-+/.test(lines[i + 1]!.trim())) {
      const header = splitTableRow(line)
      i += 2 // skip header + separator
      const rows: string[][] = []
      while (i < lines.length && lines[i]!.includes('|')) {
        rows.push(splitTableRow(lines[i]!))
        i++
      }
      blocks.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '0.75rem 0' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '0.9rem',
            }}
          >
            <thead>
              <tr>
                {header.map((cell, idx) => (
                  <th
                    key={idx}
                    style={{
                      textAlign: 'left',
                      padding: '0.4rem 0.75rem',
                      borderBottom: '2px solid var(--border)',
                      background: 'var(--bg)',
                      fontWeight: 600,
                    }}
                  >
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      style={{
                        padding: '0.4rem 0.75rem',
                        borderBottom: '1px solid var(--border)',
                        verticalAlign: 'top',
                      }}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // Bullet list
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items: string[] = []
      while (i < lines.length && (lines[i]!.startsWith('- ') || lines[i]!.startsWith('* '))) {
        // Allow wrapped continuation lines (indented under the bullet).
        let item = lines[i]!.slice(2)
        i++
        while (
          i < lines.length &&
          lines[i]!.startsWith('  ') &&
          !lines[i]!.startsWith('  -') &&
          !lines[i]!.startsWith('  *')
        ) {
          item += ' ' + lines[i]!.trim()
          i++
        }
        items.push(item)
      }
      blocks.push(
        <ul key={key++} style={{ margin: compact ? '0.4rem 0' : '0.6rem 0', paddingLeft: '1.4rem' }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: '0.25rem' }}>
              {renderInline(it)}
            </li>
          ))}
        </ul>,
      )
      continue
    }

    // Ordered list (`1.` / `2.` …) — mirrors the bullet handler.
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i]!)) {
        let item = lines[i]!.replace(/^\d+\.\s+/, '')
        i++
        while (
          i < lines.length &&
          lines[i]!.startsWith('  ') &&
          !/^\s*\d+\.\s/.test(lines[i]!) &&
          !lines[i]!.startsWith('  -') &&
          !lines[i]!.startsWith('  *')
        ) {
          item += ' ' + lines[i]!.trim()
          i++
        }
        items.push(item)
      }
      blocks.push(
        <ol key={key++} style={{ margin: compact ? '0.4rem 0' : '0.6rem 0', paddingLeft: '1.5rem' }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ marginBottom: '0.25rem' }}>
              {renderInline(it)}
            </li>
          ))}
        </ol>,
      )
      continue
    }

    // Paragraph (gather consecutive non-empty, non-special lines)
    const para: string[] = [line]
    i++
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !lines[i]!.startsWith('- ') &&
      !lines[i]!.startsWith('* ') &&
      !/^\d+\.\s+/.test(lines[i]!) &&
      !lines[i]!.trimStart().startsWith('>') &&
      !lines[i]!.match(/^#{1,4}\s/) &&
      !(lines[i]!.includes('|') && lines[i + 1] && /^\s*\|?\s*:?-+/.test(lines[i + 1]!.trim()))
    ) {
      para.push(lines[i]!)
      i++
    }
    blocks.push(
      <p key={key++} style={{ margin: compact ? '0.4rem 0' : '0.65rem 0', lineHeight: 1.55 }}>
        {renderInline(para.join(' '))}
      </p>,
    )
  }

  return <>{blocks}</>
}

function splitTableRow(line: string): string[] {
  let raw = line.trim()
  if (raw.startsWith('|')) raw = raw.slice(1)
  if (raw.endsWith('|')) raw = raw.slice(0, -1)
  return raw.split('|').map((c) => c.trim())
}

/** Inline markdown: bold, italic, inline-code, links. */
function renderInline(s: string): ReactNode[] {
  const out: ReactNode[] = []
  // Match: **bold**, *italic*, `code`, [label](url) — in priority order.
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*[^*\s]\*|\[[^\]]+\]\([^)]+\))/g
  let last = 0
  let m: RegExpExecArray | null
  let idx = 0
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index))
    const token = m[0]
    if (token.startsWith('**')) {
      out.push(<strong key={`b${idx++}`}>{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('`')) {
      out.push(
        <code
          key={`c${idx++}`}
          style={{
            background: 'var(--bg)',
            padding: '0.1rem 0.35rem',
            borderRadius: '3px',
            fontSize: '0.88em',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}
        >
          {token.slice(1, -1)}
        </code>,
      )
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      const href = linkMatch ? safeHttpUrl(linkMatch[2]) : null
      if (linkMatch && href) {
        out.push(
          <a
            key={`l${idx++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--accent)' }}
          >
            {linkMatch[1]}
          </a>,
        )
      } else if (linkMatch) {
        // Unsafe/relative target: keep the link text, drop the link.
        out.push(linkMatch[1])
      } else {
        out.push(token)
      }
    } else {
      // single-* italic
      out.push(<em key={`i${idx++}`}>{token.slice(1, -1)}</em>)
    }
    last = re.lastIndex
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}
